'use client';

import { useState, useMemo } from 'react';
import type { Recipe } from '@/types/recipe';
import { RecipeCard } from './RecipeCard';

const PROTEIN_TYPES = [
  { id: 'alle', label: 'Alle' },
  { id: 'kjøtt', label: 'Kjøtt' },
  { id: 'fisk', label: 'Fisk' },
  { id: 'vegetar', label: 'Vegetar' },
];

const TIME_FILTERS = [
  { id: 'alle', label: 'Alle' },
  { id: 'under15', label: '< 15 min' },
  { id: '15-30', label: '15–30 min' },
  { id: 'over30', label: '30+ min' },
];

const ALLERGENS = [
  { id: 'gluten', label: 'Gluten' },
  { id: 'laktose', label: 'Laktose' },
  { id: 'egg', label: 'Egg' },
  { id: 'nøtter', label: 'Nøtter' },
  { id: 'skalldyr', label: 'Skalldyr' },
  { id: 'soya', label: 'Soya' },
  { id: 'fisk', label: 'Fisk' },
];

const CATEGORIES = [
  { id: 'alle', label: 'Alle' },
  { id: 'frokost', label: 'Frokost' },
  { id: 'lunsj', label: 'Lunsj' },
  { id: 'middag', label: 'Middag' },
  { id: 'snack', label: 'Snack' },
];

export function RecipeFilter({ recipes }: { recipes: Recipe[] }) {
  const [proteinType, setProteinType] = useState('alle');
  const [timeFilter, setTimeFilter] = useState('alle');
  const [excludeAllergens, setExcludeAllergens] = useState<string[]>([]);
  const [category, setCategory] = useState('alle');
  const [search, setSearch] = useState('');

  const toggleAllergen = (id: string) => {
    setExcludeAllergens(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const filtered = useMemo(() => {
    return recipes.filter(r => {
      // Category
      if (category !== 'alle' && r.category !== category) return false;

      // Protein type
      if (proteinType === 'kjøtt' && !r.tags.some(t => ['kjøtt', 'kylling', 'biff', 'svin', 'lam', 'kjøttdeig'].includes(t))) return false;
      if (proteinType === 'fisk' && !r.tags.some(t => ['fisk', 'laks', 'torsk', 'sjømat', 'reker'].includes(t))) return false;
      if (proteinType === 'vegetar' && !r.tags.includes('vegetar')) return false;

      // Time
      if (timeFilter === 'under15' && r.totalTimeMin >= 15) return false;
      if (timeFilter === '15-30' && (r.totalTimeMin < 15 || r.totalTimeMin > 30)) return false;
      if (timeFilter === 'over30' && r.totalTimeMin <= 30) return false;

      // Allergens (exclude recipes that contain selected allergens)
      if (excludeAllergens.length > 0) {
        if (r.allergens.some(a => excludeAllergens.includes(a))) return false;
      }

      // Search
      if (search) {
        const q = search.toLowerCase();
        if (!r.name.toLowerCase().includes(q) && !r.description.toLowerCase().includes(q) && !r.tags.some(t => t.includes(q))) return false;
      }

      return true;
    });
  }, [recipes, proteinType, timeFilter, excludeAllergens, category, search]);

  return (
    <div>
      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/></svg>
          <input
            type="text"
            placeholder="Søk i oppskrifter..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-stone-400 bg-white"
          />
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap gap-6 mb-6">
        {/* Category */}
        <div>
          <div className="text-xs text-stone-400 mb-2 font-medium">Måltid</div>
          <div className="flex gap-1.5">
            {CATEGORIES.map(c => (
              <button key={c.id} onClick={() => setCategory(c.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${category === c.id ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Protein type */}
        <div>
          <div className="text-xs text-stone-400 mb-2 font-medium">Type</div>
          <div className="flex gap-1.5">
            {PROTEIN_TYPES.map(p => (
              <button key={p.id} onClick={() => setProteinType(p.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${proteinType === p.id ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Time */}
        <div>
          <div className="text-xs text-stone-400 mb-2 font-medium">Tid</div>
          <div className="flex gap-1.5">
            {TIME_FILTERS.map(t => (
              <button key={t.id} onClick={() => setTimeFilter(t.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${timeFilter === t.id ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Allergen exclusion */}
      <div className="mb-6">
        <div className="text-xs text-stone-400 mb-2 font-medium">Vis ikke oppskrifter med:</div>
        <div className="flex flex-wrap gap-1.5">
          {ALLERGENS.map(a => (
            <button key={a.id} onClick={() => toggleAllergen(a.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${excludeAllergens.includes(a.id) ? 'bg-red-50 border border-red-300 text-red-700' : 'bg-white border border-stone-200 text-stone-500 hover:bg-stone-50'}`}>
              {excludeAllergens.includes(a.id) ? '✕ ' : ''}{a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results count */}
      <div className="text-xs text-stone-400 mb-4">
        {filtered.length} {filtered.length === 1 ? 'oppskrift' : 'oppskrifter'}
        {(proteinType !== 'alle' || timeFilter !== 'alle' || excludeAllergens.length > 0 || category !== 'alle' || search) && (
          <button onClick={() => { setProteinType('alle'); setTimeFilter('alle'); setExcludeAllergens([]); setCategory('alle'); setSearch(''); }}
            className="ml-2 text-stone-500 hover:text-stone-700 underline">Nullstill filtre</button>
        )}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map(recipe => (
          <RecipeCard key={recipe.slug} recipe={recipe} />
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 text-stone-400">
          <p className="text-lg mb-2">Ingen oppskrifter matcher filtrene</p>
          <p className="text-sm">Prøv å endre filtrene eller nullstill.</p>
        </div>
      )}
    </div>
  );
}
